/**
 * Bootstrap do i18next.
 *
 * Cadeia de resolução do idioma (a preferência salva do usuário entra depois,
 * no login, via aplicarIdiomaDoUsuario):
 *   1. escolha anterior neste dispositivo (localStorage)
 *   2. idioma do navegador, se for suportado
 *   3. pt-BR
 */
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import {
    DEFAULT_LANGUAGE,
    DEFAULT_NAMESPACE,
    LANGUAGE_STORAGE_KEY,
    SUPPORTED_LANGUAGE_CODES,
    resolverIdiomaSuportado,
} from './config';
import backendDeTraducao, { namespacesDisponiveis } from './resources';

export const i18nPronto = i18n
    .use(backendDeTraducao)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        fallbackLng: DEFAULT_LANGUAGE,
        supportedLngs: SUPPORTED_LANGUAGE_CODES,
        load: 'currentOnly',

        // Todos os namespaces do idioma: o i18next só resolve o init (e cada troca de
        // idioma) com eles carregados. Carregando sob demanda, a tela aberta por link
        // direto ou F5 aparecia com as chaves cruas até o arquivo chegar. Os arquivos
        // continuam em chunks separados - o bundle inicial não muda.
        ns: namespacesDisponiveis(DEFAULT_LANGUAGE),
        defaultNS: DEFAULT_NAMESPACE,

        detection: {
            order: ['localStorage', 'navigator'],
            lookupLocalStorage: LANGUAGE_STORAGE_KEY,
            caches: ['localStorage'],
            // Garante que 'en-GB' ou 'pt' virem um idioma que existe de fato.
            convertDetectedLanguage: (idioma) =>
                resolverIdiomaSuportado(idioma) || DEFAULT_LANGUAGE,
        },

        interpolation: {
            // O React já escapa o que é renderizado.
            escapeValue: false,
        },

        react: {
            // Sem Suspense: a maior parte da aplicação é class component e não tem
            // boundary de Suspense. Como o init já traz todos os namespaces e main.jsx
            // aguarda i18nPronto, a primeira pintura sai traduzida.
            useSuspense: false,

            // Rede de segurança para namespace que chegue depois de a tela montar
            // (ex.: arquivo novo num idioma sem ele): a tela se refaz quando ele carrega.
            bindI18nStore: 'added',
        },

        // Chave sem tradução cai no fallback em vez de renderizar string vazia.
        returnEmptyString: false,
    });

/**
 * Mantém o atributo `lang` do <html> em dia com o idioma ativo.
 *
 * O index.html nasce com `lang="pt-br"` fixo. Sem isto, leitores de tela
 * pronunciariam a interface em inglês com fonética portuguesa.
 */
function sincronizarLangDoDocumento() {
    if (typeof document !== 'undefined') {
        document.documentElement.lang = idiomaAtual();
    }
}

i18n.on('languageChanged', sincronizarLangDoDocumento);
i18nPronto.then(sincronizarLangDoDocumento);

/**
 * Aplica a preferência salva do usuário, vinda do payload de login.
 *
 * `language` nulo significa que o usuário nunca escolheu um idioma — nesse caso
 * mantemos o que foi detectado (navegador ou escolha anterior no dispositivo),
 * conforme decidido no refinamento.
 */
export function aplicarIdiomaDoUsuario(language) {
    const idioma = resolverIdiomaSuportado(language);

    if (!idioma || idioma === i18n.language) {
        return Promise.resolve(i18n.language);
    }

    return i18n.changeLanguage(idioma).then(() => idioma);
}

/**
 * Troca explícita de idioma pelo usuário. O detector persiste no localStorage
 * automaticamente (caches: ['localStorage']).
 */
export function trocarIdioma(language) {
    const idioma = resolverIdiomaSuportado(language) || DEFAULT_LANGUAGE;

    return i18n.changeLanguage(idioma).then(() => idioma);
}

/** Idioma ativo, sempre uma das tags de SUPPORTED_LANGUAGES. */
export function idiomaAtual() {
    return resolverIdiomaSuportado(i18n.language) || DEFAULT_LANGUAGE;
}

export default i18n;
